/**
 * <bodi-blur-reveal> — per-word blur-in animation triggered by viewport.
 *
 * Each word fades in from blurred/translated state with staggered timing
 * for a premium text reveal effect.
 *
 * Usage:
 *   <bodi-blur-reveal data-delay="80" data-direction="bottom">
 *     <span data-word style="opacity:0">Wear</span>
 *     <span data-word style="opacity:0">Nothing</span>
 *     <span data-word style="opacity:0">Artificial</span>
 *   </bodi-blur-reveal>
 *
 * Attributes:
 *   data-delay     — stagger delay per word in ms (default: 80)
 *   data-direction — "bottom" or "top" (default: "bottom")
 */
class BodiBlurReveal extends HTMLElement {
  connectedCallback() {
    const words = this.querySelectorAll('[data-word]');
    if (!words.length) return;

    /* Check for reduced motion preference */
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      /* Skip animation, just show all words */
      words.forEach((word) => {
        word.style.opacity = '1';
      });
      return;
    }

    const delay = parseInt(this.dataset.delay || '80', 10);
    const direction = this.dataset.direction || 'bottom';
    const animationName = direction === 'top' ? 'blur-word-in-top' : 'blur-word-in';

    words.forEach((word) => {
      word.style.opacity = '0';
      word.style.display = 'inline-block';
      word.style.willChange = 'filter, opacity, transform';
    });

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      words.forEach((word, i) => {
        word.style.animation = `${animationName} 0.35s ${i * (delay / 1000)}s cubic-bezier(0.16, 1, 0.3, 1) forwards`;
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            /* Double rAF ensures the hidden state is painted before
               the animation begins, preventing instant-completion. */
            requestAnimationFrame(() => requestAnimationFrame(reveal));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(this);

    /* Failsafe: if the observer never fires (e.g. tab backgrounded at load),
       force the words visible after 1.8s so the heading is never left hidden. */
    setTimeout(() => {
      if (revealed) return;
      revealed = true;
      words.forEach((word) => {
        word.style.animation = 'none';
        word.style.opacity = '1';
        word.style.filter = 'none';
        word.style.transform = 'none';
      });
    }, 1800);
  }
}

if (!customElements.get('bodi-blur-reveal')) {
  customElements.define('bodi-blur-reveal', BodiBlurReveal);
}
